import { DateType } from '@date-io/type';
import { EditRounded } from '@mui/icons-material';
import { ClockPickerView } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Children, cloneElement } from 'react';
import _ from "underscore";
import {
    BookingScope,
    CarGroupType,
    COMPANY_WITHOUT_VAT_NR,
    DEFAULT_VALUE_PA_ID_NUMBER,
    FirstInvoiceRecipient,
    IBooleanOption,
    ICarGroup,
    ICustomer,
    ILocation,
    IWeeklyTimeRanges,
    IWeeklyTimeRangesStringified, LegalFormCode,
    RentalLocationType
} from './types';
import { ICompany, IRangeDate } from "./api/services";

export const LOCALE_DATE_FORMAT = 'YYYY-MM-DD';
export const LOCALE_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm';
export const LOCALE_ISO_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';

export const FISCAL_CODE_REGEXP = /^(?:[A-Z][AEIOU][AEIOUX]|[AEIOU]X{2}|[B-DF-HJ-NP-TV-Z]{2}[A-Z]){2}(?:[\dLMNP-V]{2}(?:[A-EHLMPR-T](?:[04LQ][1-9MNP-V]|[15MR][\dLMNP-V]|[26NS][0-8LMNP-U])|[DHPS][37PT][0L]|[ACELMRT][37PT][01LM]|[AC-EHLMPR-T][26NS][9V])|(?:[02468LNQSU][048LQU]|[13579MPRTV][26NS])B[26NS][9V])(?:[A-MZ][1-9MNP-V][\dLMNP-V]{2}|[A-M][0L](?:[1-9MNP-V][\dLMNP-V]|[0L][1-9MNP-V]))[A-Z]$/i;

export const TRUE_OPTION: IBooleanOption = {
    value: true,
    label: 'Yes'
};

export const FALSE_OPTION: IBooleanOption = {
    value: false,
    label: 'No'
};

export const BOOLEAN_OPTIONS: IBooleanOption[] = [TRUE_OPTION, FALSE_OPTION];

/* ******************************************************** */

export const isBetweenDates = (date: DateType | null, startDate: DateType | null, endDate: DateType | null) => {
    if (!date || !startDate || !endDate) {
        return false;
    }
    if (!date.isValid() || !startDate.isValid() || !endDate.isValid()) {
        return false;
    }
    return !date.isBefore(startDate) && !date.isAfter(endDate);
}

export const dayjsDateTime = () => dayjs().set('second', 0).set('millisecond', 0);

export const createFile = (data: any, fileName: string) => {
    const blob = new Blob([data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
}

export const addEditIconToEndAdornment = (endAdornment: any) => {
    const children = Children.toArray(endAdornment.props.children);
    children.push(<EditRounded />);
    return cloneElement(endAdornment, {}, children);
}

const validateTimeRanges = (timeRangeList: [number, number][], time: number) => {
    if (timeRangeList.length === 0)
        return false;
    let isOutOfTimeRangeList = true;
    timeRangeList.forEach(timeRange => {
        const [minTime, maxTime] = timeRange;
        if (Number.isNaN(minTime) || Number.isNaN(maxTime))
            return true;
        isOutOfTimeRangeList &&= (time < minTime || time > maxTime);
    });
    return !isOutOfTimeRangeList;
}

export const validateWeeklyTimeRanges = (
    date: DateType | null,
    timeRanges: IWeeklyTimeRanges
) => {
    if (!date || !date.isValid())
        return true;
    const day = date.day();
    const time = Number(date.format('HH.mm'));
    switch (day) {
        case 1:
            return validateTimeRanges(timeRanges.mon, time);
        case 2:
            return validateTimeRanges(timeRanges.tue, time);
        case 3:
            return validateTimeRanges(timeRanges.wed, time);
        case 4:
            return validateTimeRanges(timeRanges.thu, time);
        case 5:
            return validateTimeRanges(timeRanges.fri, time);
        case 6:
            return validateTimeRanges(timeRanges.sat, time);
        case 0:
            return validateTimeRanges(timeRanges.sun, time);
        default:
            return false;
    }
}

export const validateWeeklyOpenings = (
    date: DateType | null,
    timeRanges: IWeeklyTimeRanges
) => {
    if (!date || !date.isValid())
        return true;
    const day = date.day();
    switch (day) {
        case 1:
            return timeRanges.mon.length > 0;
        case 2:
            return timeRanges.tue.length > 0;
        case 3:
            return timeRanges.wed.length > 0;
        case 4:
            return timeRanges.thu.length > 0;
        case 5:
            return timeRanges.fri.length > 0;
        case 6:
            return timeRanges.sat.length > 0;
        case 0:
            return timeRanges.sun.length > 0;
        default:
            return false;
    }
}

export const validateLocationDate = (
    date: DateType | null,
    location: ILocation | null,
    locationType: RentalLocationType,
    dmsAssociatedOperator?: string[]
) => {
    if (
        !location ||
        !date ||
        (location.afterHours && locationType ===RentalLocationType.Pickup) ||
        (location.keybox && locationType === RentalLocationType.Dropoff) ||
        (locationType === RentalLocationType.Pickup && dmsAssociatedOperator?.includes(location.id) && date.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD"))
    ) {
        return true;
    }
    const specialDayOpenings = location.specialDayOpenings.find(o =>
        date.isSame(o.date, 'day')
    );
    if (specialDayOpenings) {
        const time = Number(date.format('HH.mm'));
        return validateTimeRanges(parseRange(specialDayOpenings.timeframes), time);
    }
    return (
        validateWeeklyTimeRanges(date, parseWeeklyRanges(location.openings))
    )
};

export const checkClosedDMS = (
    date: DateType | null,
    location: ILocation | null,
    locationType: RentalLocationType,
    dmsAssociatedOperator?: string[]
) => {
    if (
        !location ||
        !date ||
        (locationType === RentalLocationType.Pickup && dmsAssociatedOperator?.includes(location.id) && date.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD"))
    ) {
        return true;
    }
    const specialDayOpenings = location.specialDayOpenings.find(o =>
        date.isSame(o.date, 'day')
    );

    if (specialDayOpenings) { 
        return true; //DMS aperto
    } 

    //Verifica l'assenza di orari per la specifica data
    let isClosed = !validateWeeklyOpenings(date, parseWeeklyRanges(location.openings));
    
    //DMS risulta chiuso se per una specifica date non ci sono orari riportati e non è permesso l'afterhours/keybox
    return !(isClosed && ((!location.afterHours && locationType ===RentalLocationType.Pickup) || (!location.keybox && locationType === RentalLocationType.Dropoff)));
};

export const validateRangeDate = (
    date: DateType | null,
    rangeDate: DateType | null,
    type : RentalLocationType
) => {
    if(!rangeDate || !date) {
        return true;
    }

    if (type === RentalLocationType.Pickup) {
        return rangeDate.isBefore(date, 'day') || rangeDate.isSame(date, 'day');
    } else {
        return rangeDate.isAfter(date, 'day') || rangeDate.isSame(date , 'day');
    }
}

export const shouldDisableRentalDate = (
    date: dayjs.Dayjs,
    location: ILocation | null,
    locationType: RentalLocationType,
    dmsAssociatedOperator?: string[],
    rangeDate?:  IRangeDate) => {

    if(!_.isEmpty(rangeDate)) {
        if(locationType === RentalLocationType.Pickup) {
            return  !((!rangeDate?.startDate || date.isSame(dayjs(rangeDate?.startDate), 'day') || date.isAfter(dayjs(rangeDate?.startDate), 'day')) && (!rangeDate?.endDate || date.isBefore(dayjs(rangeDate?.endDate), 'day') || date.isSame(dayjs(rangeDate?.endDate), 'day')))
        } else {
            return !((!rangeDate?.endDate || date.isSame(dayjs(rangeDate?.endDate), 'day') || date.isBefore(dayjs(rangeDate?.endDate), 'day')) && (!rangeDate?.startDate || date.isAfter(dayjs(rangeDate?.startDate), 'day') || date.isSame(dayjs(rangeDate?.startDate), 'day')))
        }
    }

    if (
        !location ||
        (location.afterHours && locationType === RentalLocationType.Pickup)||
        (location.keybox && locationType === RentalLocationType.Dropoff) ||
        (locationType === RentalLocationType.Pickup && dmsAssociatedOperator?.includes(location.id) && date.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD"))
    ) {
        return false;
    }

    const specialDayOpenings = location.specialDayOpenings.find(o =>
        date.isSame(o.date, 'day')
    );
    if (specialDayOpenings) {
        return specialDayOpenings.timeframes === '';
    }
    switch (date.get('d')) {
        case 1:
            return location.openings.mon === '';
        case 2:
            return location.openings.tue === '';
        case 3:
            return location.openings.wed === '';
        case 4:
            return location.openings.thu === '';
        case 5:
            return location.openings.fri === '';
        case 6:
            return location.openings.sat === '';
        case 0:
            return location.openings.sun === '';
    }
    return false;
};

export const shouldDisableRentalTime = (
    time: number,
    clockTime: ClockPickerView,
    locationType: RentalLocationType,
    location: ILocation | null,
    date: dayjs.Dayjs | null,
    dmsAssociatedOperator?: string[]
) => {
    if (!location || !date) {
        return false;
    }

    if ((location.afterHours && locationType ===RentalLocationType.Pickup)||
        (location.keybox && locationType === RentalLocationType.Dropoff) ||
        (locationType === RentalLocationType.Pickup && dmsAssociatedOperator?.includes(location.id) && date.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD"))
    ) {
        return false;
    }
    const specialDayOpenings = location.specialDayOpenings.find(o =>
        date.isSame(o.date, 'day')
    );
    if (specialDayOpenings) {
        const parseTimeframes = parseRange(specialDayOpenings.timeframes);
        if (clockTime === 'hours') {
            let outOfHourRange = true;
            parseTimeframes.forEach(so => {
                const [minHour, maxHour] = so;
                outOfHourRange &&= (time < Math.floor(minHour) || time > Math.floor(maxHour));
            });
            return outOfHourRange;
        }
        else if (clockTime === 'minutes') {
            let outOfMinuteRange = true;
            parseTimeframes.forEach(so => {
                const [minHour, maxHour] = so;
                const currentTime = date.hour() + (time / 100);
                outOfMinuteRange &&= (currentTime < minHour || currentTime > maxHour);
            });
            return outOfMinuteRange;
        }
        else {
            return false;
        }
    }
    const pickupDateDayIndex = date.get('d');
    let selectedOpenings: [number, number][] = [];
    const parsedOpenings = parseWeeklyRanges(location.openings);
    switch (pickupDateDayIndex) {
        case 1:
            selectedOpenings = parsedOpenings.mon;
            break;
        case 2:
            selectedOpenings = parsedOpenings.tue;
            break;
        case 3:
            selectedOpenings = parsedOpenings.wed;
            break;
        case 4:
            selectedOpenings = parsedOpenings.thu;
            break;
        case 5:
            selectedOpenings = parsedOpenings.fri;
            break;
        case 6:
            selectedOpenings = parsedOpenings.sat;
            break;
        case 0:
            selectedOpenings = parsedOpenings.sun;
            break;
    }
    if (clockTime === 'hours') {
        let outOfHourRange = true;
        selectedOpenings.forEach(so => {
            const [minHour, maxHour] = so;
            outOfHourRange &&= (time < Math.floor(minHour) || time > Math.floor(maxHour));
        });
        return outOfHourRange;
    }
    else if (clockTime === 'minutes') {
        let outOfMinuteRange = true;
        selectedOpenings.forEach(so => {
            const [minHour, maxHour] = so;
            const currentTime = date.hour() + (time / 100);
            outOfMinuteRange &&= (currentTime < minHour || currentTime > maxHour);
        });
        return outOfMinuteRange;
    }
    else {
        return false;
    }
}

export const parseRange = (openingString: string | null) => {
    if (!openingString)
        return []
    if (openingString === '')
        return [];
    let result: [number, number][] = [];
    const timeRangeList = openingString.split(';');
    timeRangeList.forEach(tr => {
        const [minTime, maxTime] = tr.split('-').map(t => {
            const [hour, minutes] = t.split(':');
            return Number(hour) + Number(minutes) / 100;
        });
        if (!Number.isNaN(minTime) && !Number.isNaN(maxTime))
            result.push([Number(minTime), Number(maxTime)]);
    });
    return result;
}

export const parseWeeklyRanges = (weeklyRangesStringified: IWeeklyTimeRangesStringified): IWeeklyTimeRanges => {
    return {
        mon: parseRange(weeklyRangesStringified.mon),
        tue: parseRange(weeklyRangesStringified.tue),
        wed: parseRange(weeklyRangesStringified.wed),
        thu: parseRange(weeklyRangesStringified.thu),
        fri: parseRange(weeklyRangesStringified.fri),
        sat: parseRange(weeklyRangesStringified.sat),
        sun: parseRange(weeklyRangesStringified.sun)
    }
};

export const getTotalRentalDays = (pickupDate: dayjs.Dayjs | null, dropoffDate: dayjs.Dayjs | null) => {
    if (!pickupDate || !dropoffDate) {
        return 0;
    }
    const diff = dropoffDate.diff(pickupDate, 'days', true);
    if (diff < 0 || Number.isNaN(diff))
        return 0;
    return Math.ceil(diff);
};

export const getDirtyValues = (
    dirtyFields: any,
    values: any
): any => {

    if (dirtyFields === true || Array.isArray(dirtyFields)) {
        return values;
    }

    return Object.fromEntries(
        Object.keys(dirtyFields).map((key) => [
            key,
            getDirtyValues(dirtyFields[key], values[key])
        ])
    );
}

/**
 * Get Acriss/Sipp code
 * @param  {ICarGroup} carGroup
 */
export const getCarGroupCode = (carGroup: ICarGroup) => carGroup.type === CarGroupType.Acriss ? carGroup.acrissCode : carGroup.sippCode;

/**
 * Get Acriss/Sipp code with label
 * @param  {ICarGroup} carGroup
 */
export const getCarGroupCodewithLabel = (carGroup: ICarGroup) => carGroup.type === CarGroupType.Acriss ? carGroup.acrissCode + ' - ' + carGroup.acrissLabel : carGroup.sippCode + ' - ' + carGroup.sippLabel;

export const hasScope = (globalData: any, scope: BookingScope) => {
    return (
        globalData.user.profiles
            .some((p: any) => (
                p.auths
                    .some((a: any) => a.name === scope)
            ))
    )
}

export const setDecimalSeparator = (value: string, language: string) => {

    if(language === 'en') {
        return value;
    } else {
        return value.replace(".",",")
    }
}

export const setInvoicePropertyCompanyByICompany = (company: ICompany) => {
    return ({
        'invoice.linkedToBusinessAgreement': company.linkedToBusinessAgreement,
        'invoice.id': company.customerId ?? '',
        'invoice.companyName': company.companyName ?? '',
        'invoice.fiscalCode': company.fiscalCode ?? '',
        'invoice.email': company.email ?? '',
        'invoice.billingCountry': company.country ?? '',
        'invoice.billingCity': company.city ?? '',
        'invoice.billingStreetName': company.streetName ?? '',
        'invoice.billingStreetNumber': company.streetNumber ?? '',
        'invoice.billingPostalCode': company.postalCode ?? '',
        'invoice.certifiedEmail': company.certifiedEmail ?? '',
        'invoice.vatNumber': company.vatNumber ?? '',
        'invoice.vatNumberCountry': company.vatNumberCountry ?? '',
        'invoice.siren': company.siren ?? '',
        'invoice.siret': company.siret ?? '',
        'invoice.invoiceCode': company.invoiceCode ?? '',
        'invoice.uuid': company.uuid ?? '',
        'invoice.billingProvince': company.province ?? '',
        'invoice.legalEntity': company.legalEntity ?? null,
        'invoice.publicAuthority': company.publicAuthority === '1',
        'invoice.paIdNumber': company.publicAuthority === '1' ? (!!company.paIdNumber ? company.paIdNumber : DEFAULT_VALUE_PA_ID_NUMBER) : ''
    })
}

export const setBookingOwnerPropertyCompanyCustomer = (company: ICompany) => {
    return ({
        'bookingOwner.linkedToBusinessAgreement': company.linkedToBusinessAgreement,
        'bookingOwner.id': company.customerId ?? '',
        'bookingOwner.companyName': company.companyName ?? '',
        'bookingOwner.fiscalCode': company.fiscalCode ?? '',
        'bookingOwner.email': company.email ?? '',
        'bookingOwner.country': company.country ?? '',
        'bookingOwner.city': company.city ?? '',
        'bookingOwner.streetName': company.streetName ?? '',
        'bookingOwner.streetNumber': company.streetNumber ?? '',
        'bookingOwner.postalCode': company.postalCode ?? '',
        'bookingOwner.certifiedEmail': company.certifiedEmail ?? '',
        'bookingOwner.vatNumber': company.vatNumber ?? '',
        'bookingOwner.vatNumberCountry': company.vatNumberCountry ?? '',
        'bookingOwner.siren': company.siren ?? '',
        'bookingOwner.siret': company.siret ?? '',
        'bookingOwner.invoiceCode': company.invoiceCode ?? '',
        'bookingOwner.telephone': company.telephone ?? '',
        'bookingOwner.mobile': company.mobile ?? '',
        'bookingOwner.uuid': company.uuid ?? '',
        'bookingOwner.legalEntity': company.legalEntity ?? null,
        'bookingOwner.mobilePrefix': null,
        'bookingOwner.publicAuthority': company.publicAuthority === '1',
        'bookingOwner.paIdNumber': company.publicAuthority === '1' ? (!!company.paIdNumber ? company.paIdNumber : DEFAULT_VALUE_PA_ID_NUMBER) : ''
    })
}

export const setBookingOwnerPropertyPersonCustomer = (person: ICustomer) => {
    return ({
        'bookingOwner.linkedToBusinessAgreement': person.linkedToBusinessAgreement,
        'bookingOwner.id': person.customerId ?? '',
        'bookingOwner.companyName': person.companyName ?? '',
        'bookingOwner.fiscalCode': person.fiscalCode ?? '',
        'bookingOwner.email': person.email ?? '',
        'bookingOwner.country': person.billingCountry ?? '',
        'bookingOwner.city': person.billingCity ?? '',
        'bookingOwner.streetName': person.billingStreetName ?? '',
        'bookingOwner.streetNumber': person.billingStreetName ?? '',
        'bookingOwner.postalCode': person.billingPostalCode ?? '',
        'bookingOwner.certifiedEmail': person.certifiedEmail ?? '',
        'bookingOwner.vatNumber': person.vatNumber ?? '',
        'bookingOwner.vatNumberCountry': person.vatNumberCountry ?? '',
        'bookingOwner.invoiceCode': person.invoiceCode ?? '',
        'bookingOwner.telephone': person.telephone ?? '',
        'bookingOwner.mobile': person.mobile ?? '',
        'bookingOwner.uuid': person.uuid ?? '',
        'bookingOwner.legalEntity': person.legalEntity ?? null,
        'bookingOwner.mobilePrefix': null,
        'bookingOwner.publicAuthority': person.publicAuthority === '1',
        'bookingOwner.paIdNumber': person.publicAuthority === '1' ? (!!person.paIdNumber ? person.paIdNumber : DEFAULT_VALUE_PA_ID_NUMBER) : '',
        'bookingOwner.name': person.firstName ?? '',
        'bookingOwner.surname': person.lastName ?? '',
        'bookingOwner.birthDate': person.birthDate !== '' ? dayjs(person.birthDate, 'YYYY-MM-DD') : null,
        'bookingOwner.birthPlace': person.birthPlace ?? '',
        'bookingOwner.birthNation': person.birthNation ?? '',
        'bookingOwner.residenceCountry': person.residenceCountry ?? '',
        'bookingOwner.drivingLicenseNumber': person.drivingLicenseNumber ?? ''
    })
}

export const setInvoicePropertyPersonCustomer = (person: ICustomer) => {
    return ({
        'invoice.id': person.customerId,
        'invoice.name': person.firstName ?? '',
        'invoice.surname': person.lastName ?? '',
        'invoice.birthDate': person.birthDate !== '' ? dayjs(person.birthDate, 'YYYY-MM-DD') : null,
        'invoice.birthNation': person.birthNation ?? '',
        'invoice.birthPlace': person.birthPlace ?? '',
        'invoice.residenceCountry': person.residenceCountry ?? '',
        'invoice.fiscalCode': person.fiscalCode ?? '',
        'invoice.drivingLicenseNumber': person.drivingLicenseNumber ?? '',
        'invoice.mobile': person.mobile ?? '',
        'invoice.email': person.email ?? '',
        'invoice.certifiedEmail': person.certifiedEmail ?? '',
        'invoice.uuid': person.uuid ?? '',
        'invoice.mobilePrefix': null,
        'invoice.billingCountry': person.billingCountry ?? '',
        'invoice.billingProvince': person.billingProvince ?? '',
        'invoice.billingCity': person.billingCity ?? '',
        'invoice.billingStreetName': person.billingStreetName ?? '',
        'invoice.billingStreetNumber': person.billingStreetNumber ?? '',
        'invoice.billingPostalCode': person.billingPostalCode ?? '',
    })
}

export const setInvoicePropertyCompanyByICustomer = (company: ICustomer) => {
        return ({
            'invoice.id': company.customerId,
            'invoice.linkedToBusinessAgreement': company.linkedToBusinessAgreement,
            'invoice.companyName': company.companyName ?? '',
            'invoice.fiscalCode': company.fiscalCode ?? '',
            'invoice.email': company.email ?? '',
            'invoice.billingCountry': company.billingCountry ?? '',
            'invoice.billingProvince': company.billingProvince ?? '',
            'invoice.billingCity': company.billingCity ?? '',
            'invoice.billingStreetName': company.billingStreetName ?? '',
            'invoice.billingStreetNumber': company.billingStreetNumber ?? '',
            'invoice.billingPostalCode': company.billingPostalCode ?? '',
            'invoice.certifiedEmail': company.certifiedEmail ?? '',
            'invoice.vatNumber': company.vatNumber ?? '',
            'invoice.vatNumberCountry': company.vatNumberCountry ?? '',
            'invoice.siren': company.siren ?? '',
            'invoice.siret': company.siret ?? '',
            'invoice.invoiceCode': company.invoiceCode ?? '',
            'invoice.uuid': company.uuid ?? '',
            'invoice.mobilePrefix': null,
            'invoice.legalEntity': company.legalEntity ?? null,
            'invoice.publicAuthority': company.publicAuthority === '1',
            'invoice.paIdNumber': company.publicAuthority === '1' ? (!!company.paIdNumber ?  company.paIdNumber : DEFAULT_VALUE_PA_ID_NUMBER) : ''
        })
}

export const clearFieldInvoice = () => {
    return({
        'invoice.id': '',
        'invoice.name': '',
        'invoice.surname': '',
        'invoice.birthDate': null,
        'invoice.birthNation': '',
        'invoice.birthPlace': '',
        'invoice.residenceCountry': '',
        'invoice.fiscalCode': '',
        'invoice.drivingLicenseNumber': '',
        'invoice.email': '',
        'invoice.mobile': '',
        'invoice.billingCountry': '',
        'invoice.billingCity': '',
        'invoice.billingStreetName': '',
        'invoice.billingStreetNameGoogleSearch': '',
        'invoice.billingStreetNumber': '',
        'invoice.billingPostalCode': '',
        'invoice.certifiedEmail': '',
        'invoice.vatNumber': '',
        'invoice.vatNumberCountry': '',
        'invoice.invoiceCode': '',
        'invoice.companyName': '',
        'invoice.uuid': '',
        'invoice.mobilePrefix': null,
        'invoice.billingProvince': '',
        'invoice.linkedToBusinessAgreement': false,
        'invoice.legalEntity': null,
        'invoice.publicAuthority': false,
        'invoice.paIdNumber': ''
    })
}

/**
 * Get element address from selected google api response
 * @param {any[]} apiResponse
 * @param {string[]} filters
 * @return string[]
 */
export const getAddressFromApiGoogle = (apiResponse: any[], filters: string[]): any[]  => {
    return filters?.map((filter: string) => apiResponse?.find((elem: any) => elem.types.includes(filter)));
}

/**
 * Check company code has required Vat number/Vat country
 * @param {LegalFormCode | '' | null | undefined} legalEntity
 * @return boolean
 */
export const getRequiredVatNr = (legalEntity: LegalFormCode | '' | null | undefined): boolean => {
    if(!!legalEntity){
        return !COMPANY_WITHOUT_VAT_NR.includes(legalEntity);
    }
    return true;
}
